<template>
  <AppDialogContent
    v-model="dialog"
    :loading="mutationLoading"
    :persistent="mutationLoading"
    :disable-hash-history="disableHashHistory"
    @submit-form="cloneEvent"
  >
    <template #header>{{
      $t("events:EventCardLarge.CloneModal.heading", "Clone event")
    }}</template>

    <template v-if="$apollo.queries.event.loading">
      <AppLoadingPage />
    </template>

    <v-form v-else :disabled="mutationLoading">
      <v-radio-group v-model="form.cloningType">
        <v-radio
          :label="
            $t(
              'events:EventCardLarge.CloneModal.cloneAsNewEvent.label',
              'Clone whole event as new event'
            )
          "
          value="cloneFull"
        />
        <v-radio
          :label="
            $t(
              'events:EventCardLarge.CloneModal.cloneScheduleProductsProvisions.labe',
              'Clone schedule, products and/or special provisions to existing event'
            )
          "
          value="cloneSelective"
        />
      </v-radio-group>

      <template v-if="form.cloningType == 'cloneSelective'">
        <template v-if="!event.noncompetition">
          <div class="d-flex align-center">
            <div>
              <v-checkbox
                v-model="form.partsToClone"
                :label="
                  $t(
                    'events:EventCardLarge.CloneModal.partsToClone.SCHEDULE.label',
                    'Clone competitions'
                  )
                "
                class="mt-0"
                value="SCHEDULE"
                data-test="checkboxSelective"
                hide-details
              />
            </div>

            <div v-if="form.partsToClone.includes('SCHEDULE')" class="pl-1">
              <v-btn
                icon
                x-small
                size="x-small"
                density="comfortable"
                outlined
                variant="outlined"
                @click="
                  () => (showCompetitionSelections = !showCompetitionSelections)
                "
              >
                <v-icon size="small">{{
                  showCompetitionSelections ? "$iconCaretUp" : "$iconCaretRight"
                }}</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-show="showCompetitionSelections" class="mt-5">
            <EventCompetitionSelectionsClone
              :event="event"
              :selected-competitions.sync="selectedCompetitions"
            />
          </div>
        </template>

        <v-checkbox
          v-model="form.partsToClone"
          :label="
            $t(
              'events:EventCardLarge.CloneModal.partsToClone.PRODUCTS.label',
              'Clone products and services'
            )
          "
          value="PRODUCTS"
          data-test="checkboxSelective"
          hide-details
        />

        <v-checkbox
          v-model="form.partsToClone"
          :label="
            $t(
              'events:EventCardLarge.CloneModal.partsToClone.INFO_FIELDS.label',
              'Clone special provisions (textual information)'
            )
          "
          value="INFO_FIELDS"
          data-test="checkboxSelective"
          hide-details
        />

        <br />

        <h3 class="mb-2">
          {{
            $t(
              "events:EventCardLarge.CloneModal.selectOrganiser.heading",
              "Select organiser of the target event for cloning"
            )
          }}
        </h3>
        <v-autocomplete
          v-model="form.organiserIds"
          :label="
            $t(
              'events:EventCardLarge.CloneModal.selectOrganiser.label',
              'Select organiser of the event to clone to'
            )
          "
          :hint="
            $t(
              'events:EventCardLarge.CloneModal.selectOrganiser.hint',
              'Start typing organiser\'s name. Only the organisers of whose events you can manage, will appear in search results.'
            )
          "
          :loading="
            $apollo.queries.limitedOrganiserIds.loading ||
            $apollo.queries.organisations.loading
          "
          data-test="organiserIds"
          hide-no-data
          multiple
          clearable
          :items="organisations"
          item-value="id"
          item-text="name"
          outlined
          persistent-hint
          :search-input.sync="organiserKeyword"
          @input="organiserKeyword = null"
        />
        <br />
        <h3 class="mb-2">
          {{
            $t(
              "events:EventCardLarge.CloneModal.selectEvent.heading",
              "Select target event for cloning"
            )
          }}
        </h3>
        <v-autocomplete
          v-model="form.cloneToEvent"
          data-test="cloneToEvent"
          :label="
            $t(
              'events:EventCardLarge.CloneModal.selectEvent.label',
              'Select event to clone to'
            )
          "
          :hint="
            $t(
              'events:EventCardLarge.CloneModal.selectEvent.hint',
              'Start typing event name or organiser\'s name or event place. Only future events of the same discipline and at least in same competition level and without competition classes, products or special provisions will appear in search results. '
            )
          "
          item-value="id"
          clearable
          hide-no-data
          return-object
          :filter="item => item"
          :search-input.sync="eventKeyword"
          :loading="$apollo.queries.events.loading"
          :items="events"
          outlined
          persistent-hint
        >
          <template #selection="{ item }">
            <span
              style="
                white-space: nowrap;
                overflow: clip;
                width: 90%;
                text-overflow: ellipsis;
              "
            >
              {{ truncate(item.name, { length: 30 }) }} /
              {{ $dayjs.nicerDate(item.startDate, item.endDate) }} /
              {{ item.organiser.nameShort || item.organiser.name }} /
              {{ item.place }}
            </span>
          </template>
          <template #item="{ item }">
            {{ truncate(item.name, { length: 30 }) }} /
            {{ $dayjs.nicerDate(item.startDate, item.endDate) }} /
            {{ item.organiser.nameShort || item.organiser.name }} /
            {{ item.place }}
          </template>
        </v-autocomplete>

        <br />
      </template>

      <div v-if="form.cloningType == 'cloneFull'" data-test="cloneFullContent">
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.intro.text",
              "You are going to clone this show.  We will make a copy of show details, classes, products and special provision section."
            )
          }}
        </p>
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.PleaseNote.text",
              "Please note:"
            )
          }}
        </p>
        <ul>
          <li>
            {{
              $t(
                "events:EventCardLarge.CloneModal.bullet1.text",
                'The name of your new show will start with "Copy of...". Don\'t forget to change it.'
              )
            }}
          </li>

          <li>
            {{
              $t(
                "events:EventCardLarge.CloneModal.bullet2.text",
                "The dates of your new show will be two month starting from today."
              )
            }}
          </li>
          <li>
            {{
              $t(
                "events:EventCardLarge.CloneModal.bullet3.text",
                "We do not clone cup's information, bacause most probably this data would change each year based on Federation decisions."
              )
            }}
          </li>
          <li>
            {{
              $t(
                "events:EventCardLarge.CloneModal.bullet4.text",
                "The documents will also not be copied."
              )
            }}
          </li>
        </ul>

        <EventCompetitionEntryRulesClone
          v-if="canCloneEntryRules"
          v-model="form.cloneCompetitionEntryRules"
        />

        <br />
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.outro.text",
              "Please review the cloned show and make all necessary changes before proposing it to calendar."
            )
          }}
          <br /><br />
          <b>
            {{
              $t(
                "events:EventCardLarge.CloneModal.ConfirmationQuestion.text",
                "Do you confirm cloning this show?"
              )
            }}</b
          >
        </p>
      </div>
      <div
        v-else-if="form.cloningType == 'cloneSelective'"
        data-test="cloneSelectiveContent"
      >
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.cloneScheduleProductsProvisions.intro.text",
              "You are going to clone schedule, including all competitions with details, products and specials provisions to the selected event. Are you sure?"
            )
          }}
        </p>
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.PleaseNote.text",
              "Please note:"
            )
          }}
        </p>
        <ul>
          <template v-if="form.partsToClone.includes('SCHEDULE')">
            <li data-test="scheduleBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.SCHEDULE.bullet1.text",
                  "All competitions will be cloned to the target show"
                )
              }}
            </li>
            <li data-test="scheduleBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.SCHEDULE.bullet2.text",
                  "We do not clone information on cups and series as this information might change from year to year"
                )
              }}
            </li>
            <li data-test="scheduleBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.SCHEDULE.bullet3.text",
                  "If some of the codes for levels or judgements were disabled, we will not clone them"
                )
              }}
            </li>
          </template>
          <template v-if="form.partsToClone.includes('PRODUCTS')">
            <li data-test="productsBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.PRODUCTS.bullet1.text",
                  "All products will be cloned to the target show"
                )
              }}
            </li>
            <li data-test="productsBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.PRODUCTS.bullet2.text",
                  "We do not clone special registration deadlines for products"
                )
              }}
            </li>
          </template>
          <template v-if="form.partsToClone.includes('INFO_FIELDS')">
            <li data-test="infoFieldsBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.INFO_FIELDS.bullet1.text",
                  "All textual information in special provisions will be cloned to the target show"
                )
              }}
            </li>
            <li data-test="infoFieldsBullet">
              {{
                $t(
                  "events:EventCardLarge.CloneModal.partsToClone.INFO_FIELDS.bullet2.text",
                  "If some chapters were disabled at federation level, we will not clone them"
                )
              }}
            </li>
          </template>
        </ul>

        <EventCompetitionEntryRulesClone
          v-if="canCloneEntryRules"
          v-model="form.cloneCompetitionEntryRules"
        />

        <br />
        <p>
          {{
            $t(
              "events:EventCardLarge.CloneModal.cloneScheduleProductsProvisions.outro.text",
              "Please review the target event and make all necessary changes before proposing it to calendar or for opening an online registration."
            )
          }}
          <br /><br />
          <b v-if="form.cloneToEvent && form.cloneToEvent.id">
            {{
              $t(
                [
                  "events:EventCardLarge.CloneModal.cloneScheduleProductsProvisions.outro.ConfirmationQuestion.text",
                  "Do you confirm cloning of selected parts from {\{- cloneFromEventNameWithDetails }\} to {\{- cloneToEventNameWithDetails  }\}?"
                ],
                {
                  cloneFromEventNameWithDetails: event.displayNameWithDetails,
                  cloneToEventNameWithDetails:
                    form.cloneToEvent.displayNameWithDetails
                }
              )
            }}
          </b>
        </p>
      </div>
    </v-form>
  </AppDialogContent>
</template>
<script>
import { set, ref } from "vue";
import gql from "fraql";
import { truncate, orderBy } from "lodash-es";
import AppDialogContent from "@elements/AppDialogContent.vue";
import { useCompetitionLevelHelper } from "@composables/masterdata/competitions/competitionLevels";
import { useCompetitionEntryRuleTemplatesHelper } from "@composables/masterdata/competitions/competitionEntryRuleTemplates";
import EventCompetitionEntryRulesClone from "./EventCompetitionEntryRulesClone.vue";
import EventCompetitionSelectionsClone from "./EventCompetitionSelectionsClone.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";

export default {
  name: "EventCloneDialog",
  components: {
    AppLoadingPage,
    AppDialogContent,
    EventCompetitionEntryRulesClone,
    EventCompetitionSelectionsClone
  },
  props: {
    eventId: {
      type: String,
      required: true
    },
    disableHashHistory: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const showCompetitionSelections = ref(false);

    return {
      ...useCompetitionEntryRuleTemplatesHelper(),
      ...useCompetitionLevelHelper(),
      truncate,

      showCompetitionSelections
    };
  },
  data() {
    return {
      dialogValue: false,
      mutationLoading: false,
      organiserKeyword: null,
      eventKeyword: null,
      selectedCompetitions: {},

      limitedOrganiserIds: [],
      organisations: [],
      events: [],
      event: {
        noncompetition: false,
        highestCompetitionLevelId: null,
        competitions: []
      },

      form: {
        cloningType: "cloneFull",
        partsToClone: ["SCHEDULE", "PRODUCTS", "INFO_FIELDS"],
        organiserIds: [],
        cloneCompetitionEntryRules: true,
        cloneToEvent: {
          displayNameWithDetails: null
        }
      }
    };
  },
  computed: {
    dialog: {
      get() {
        return this.dialogValue;
      },
      set(v) {
        this.form = JSON.parse(
          JSON.stringify({
            cloningType: "cloneFull",
            partsToClone: ["SCHEDULE", "PRODUCTS", "INFO_FIELDS"],
            organiserIds: [],
            cloneCompetitionEntryRules: true,
            cloneToEvent: {
              displayNameWithDetails: null
            }
          })
        );
        this.dialogValue = v;
      }
    },
    eventHigherCompetitionLevelIds() {
      if (this.event.noncompetition || !this.event.highestCompetitionLevelId)
        return [];

      const { category, rank } = this.event.highestCompetitionLevel;
      return this.masterCompetitionLevelsActive
        .filter(x => x.category == category && x.rank >= rank)
        .map(x => x.id);
    },
    mutationName() {
      return this.event.noncompetition
        ? "noncompetitionEventCreateOrUpdate"
        : "eventCreateOrUpdate";
    },
    canCloneEntryRules() {
      let answer =
        this.event.competitions.some(
          c => c.competitionEntryRules?.length > 0
        ) ||
        this.masterCompetitionEntryRuleTemplatesActive.some(
          c => c.disciplineId == this.event.disciplineId
        );

      if (this.form.cloningType == "cloneFull") return answer;
      return answer && this.form.partsToClone.includes("SCHEDULE");
    }
  },
  apollo: {
    event: {
      query: gql`
        query eventCloneDialog($id: ID) {
          event(id: $id) {
            id
            startDate
            endDate
            displayNameWithDetails
            noncompetition
            disciplineId
            highestCompetitionLevelId
            highestCompetitionLevel @client {
              id
              rank
              category
            }
            organiser {
              id
              name
              shortName
            }
            ${EventCompetitionSelectionsClone.fragments.eventFields}
            competitions {
              id
              ${EventCompetitionSelectionsClone.fragments.competitionFields}
              competitionEntryRules {
                id
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.eventId
        };
      },
      skip() {
        return this.dialogValue == false;
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        set(this, "event", data.event);

        data.event.competitions.forEach(c => {
          set(this.selectedCompetitions, c.id, true);
        });
      }
    },
    organisations: {
      query: gql`
        query eventCloneOrganisersSelection(
          $organisationIds: [ID]
          $keyword: String
        ) {
          organisations(organisationIds: $organisationIds, keyword: $keyword) {
            id
            name
          }
        }
      `,
      skip() {
        return !this.organiserKeyword || this.organiserKeyword.length <= 2;
      },
      variables() {
        return {
          organisationIds: this.limitedOrganiserIds,
          keyword: this.organiserKeyword
        };
      }
    },
    limitedOrganiserIds: {
      query: gql`
        query mutationAllowedForOrganisationIds($mutationName: String!) {
          mutationAllowedForOrganisationIds(mutationName: $mutationName)
        }
      `,
      skip() {
        return !this.$auth.checkUserCanPerformMutationsInScopeAny(
          this.mutationName
        );
      },
      manual: true,
      variables() {
        return {
          mutationName: this.mutationName
        };
      },
      result({ data, loading }) {
        if (loading || !data) return;
        this.organiserIds = data.mutationAllowedForOrganisationIds ?? [];
      },
      fetchPolicy: "cache-first"
    },
    events: {
      query: gql`
        query eventsCloneSelection(
          $disciplineIds: [ID]
          $competitionLevelIds: [ID]
          $startDateFrom: String
          $statuses: [String]
          $organiserIds: [ID]
          $noCompetitions: Boolean
          $noProducts: Boolean
          $noInfoFields: Boolean
          $noncompetition: Boolean
          $keyword: String
        ) {
          events(
            disciplineIds: $disciplineIds
            competitionLevelIds: $competitionLevelIds
            startDateFrom: $startDateFrom
            statuses: $statuses
            organiserIds: $organiserIds
            noCompetitions: $noCompetitions
            noProducts: $noProducts
            noInfoFields: $noInfoFields
            noncompetition: $noncompetition
            keyword: $keyword
          ) {
            id
            name
            displayNameWithDetails
            startDate
            endDate
            organiser {
              id
              name
              shortName
            }
            place
          }
        }
      `,
      debounce: 500,
      skip() {
        return this.form.cloningType != "cloneSelective";
      },
      update({ events: evs }) {
        const events = JSON.parse(JSON.stringify(evs));
        const organiserOnes = orderBy(
          events.filter(x => x.organiser.id == this.event.organiser.id),
          "startDate",
          "asc"
        );
        const others = orderBy(
          events.filter(x => x.organiser.id != this.event.organiser.id),
          "startDate",
          "asc"
        );
        return [...organiserOnes, ...others];
      },
      variables() {
        return {
          disciplineIds: [this.event.disciplineId],
          competitionLevelIds: this.eventHigherCompetitionLevelIds,
          startDateFrom: this.$dayjs().format("YYYY-MM-DD"),
          statuses: ["draft", "proposed", "published"],
          organiserIds: this.form.organiserIds,
          noCompetitions: this.form.partsToClone.includes("SCHEDULE"),
          noProducts: this.form.partsToClone.includes("PRODUCTS"),
          noInfoFields: this.form.partsToClone.includes("INFO_FIELDS"),
          noncompetition: this.event.noncompetition,
          keyword: this.eventKeyword
        };
      }
    }
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
    async cloneEvent() {
      try {
        this.mutationLoading = true;
        if (!this.canCloneEntryRules) {
          this.form.cloneCompetitionEntryRules = null;
        }

        let onlyCompetitionIds = undefined;
        if (
          this.form.cloningType == "cloneSelective" &&
          this.form.partsToClone.includes("SCHEDULE")
        ) {
          onlyCompetitionIds = Object.entries(this.selectedCompetitions).reduce(
            (arr, [k, v]) => {
              if (v === true) {
                arr.push(k);
              }
              return arr;
            },
            []
          );
        }

        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation eventClone(
              $id: ID!
              $partsToClone: [String!]
              $cloneToEventId: ID
              $cloneCompetitionEntryRules: Boolean
              $onlyCompetitionIds: [ID!]
            ) {
              eventClone(
                input: {
                  id: $id
                  partsToClone: $partsToClone
                  cloneToEventId: $cloneToEventId
                  cloneCompetitionEntryRules: $cloneCompetitionEntryRules
                  onlyCompetitionIds: $onlyCompetitionIds
                }
              ) {
                event {
                  id
                  noncompetition
                }
              }
            }
          `,
          variables: {
            id: this.eventId,
            partsToClone: this.form.partsToClone,
            cloneToEventId: this.form.cloneToEvent?.id,
            cloneCompetitionEntryRules: this.form.cloneCompetitionEntryRules,
            onlyCompetitionIds
          }
        });

        this.$router.push({
          name: data.eventClone.event.noncompetition
            ? "NoncompetitionEventProfilePage"
            : "EventProfilePage",
          params: { eventId: data.eventClone.event.id }
        });

        this.$root.$snackbar("success", {
          text: this.$t(
            "events:mutations.eventClone.feedback.success.text",
            "Event cloning success!"
          )
        });
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.mutationLoading = false;
      }
    }
  }
};
</script>
